import {announce} from '@github-ui/aria-live'
import {useMutation, useQueryClient} from '@github-ui/react-query'

import {LABELS} from '../constants'
import {useSavedViewEndpoints} from '../contexts/SavedViewEndpointsContext'
import type {CreateSavedViewResponse, CreateSavedViewVariables} from '../types'
import {
  primeSavedViewCaches,
  type SavedViewMutationOptions,
  sendSavedViewMutationJson,
} from '../utils/saved-view-mutations'

export function useCreateSavedViewMutation<TConfiguration = never>({owner, repo}: SavedViewMutationOptions) {
  const queryClient = useQueryClient()
  const {basePath, listUrl} = useSavedViewEndpoints(owner, repo)

  return useMutation<CreateSavedViewResponse<TConfiguration>, Error, CreateSavedViewVariables<TConfiguration>>({
    mutationFn: variables =>
      sendSavedViewMutationJson<CreateSavedViewResponse<TConfiguration>>(
        `${basePath}.json`,
        {method: 'POST', body: variables},
        LABELS.views.createError,
      ),
    // Primes the single-view cache so the post-create redirect renders immediately, then invalidates the list.
    onSuccess: ({savedView}) => primeSavedViewCaches({queryClient, owner, repo, basePath, listUrl}, savedView),
    onError: () => announce(LABELS.views.createError, {assertive: true}),
  })
}
